import { Button, Col, DatePicker, Empty, Row, Select, Spin } from 'antd';
import React, { Fragment, useContext, useEffect, useState } from 'react'
import { Label } from '../../../../components/Label/Label';
import moment, { Moment } from 'moment';
import { DatasetRecord } from '../../Store/dataTypes';
import { DT0Format, DateFormat } from '../../../../generic/StaticVariable';
import { AnalyticsDownloadLinks, ChartBox } from '../ChartBox';
import { IGetBasisChartV2ApiPayload } from '../../../../typings/chart';
import { IEnrichedMetadata } from '../../../../typings/metadata';
import { IBestDataset, IItemsValidation } from '../../../../typings/analytics';
import { MarkerItem } from '../../../../typings/home';
import { DownloadCheckboxKey } from '../../../../generic/common';
import { StoreContext } from '../../../../store/store';
import styles from './Plot.module.scss'
import Api from '../../../../api/Api';
import { LoadingOutlined } from '@ant-design/icons';

interface IScatterPlot {
  activePointMetadata?: DatasetRecord
  validInSituPoint?: boolean
  dateRange?: [moment.Moment, moment.Moment]
  activePoint?: MarkerItem
  enrichedMetadata?: IEnrichedMetadata[]
  insituName?: string
  listParameters: IItemsValidation[],
  activeTab: number
  activeCategoryKey: string
  setActiveCategory: Function
}

const ScatterPlot: React.FC<IScatterPlot> = ({
  activeCategoryKey,
  setActiveCategory,
  activePointMetadata,
  validInSituPoint,
  activePoint,
  enrichedMetadata,
  insituName,
  listParameters,
  dateRange,
  activeTab
}) => {
  const [variable, setVariable] = useState('')
  const [scatterPlotImages, setScatterPlotImages] = useState('')
  const [links, setLinks] = useState([])
  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [endDate, setEndDate] = useState<Moment | null>(null);
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (enrichedMetadata && listParameters.length > 0 && activePointMetadata?.userGroupPermissions && validInSituPoint && dateRange) {
      const parameter = listParameters[0].name
      setVariable(parameter)
      setStartDate(moment(dateRange[0]))
      setEndDate(moment(dateRange[1]))
      const values: IGetBasisChartV2ApiPayload = {
        datasetName: activePointMetadata.metadata.DatasetId,
        parameters: parameter,
        startDate: startDate?.format(DT0Format) ?? moment(dateRange[0]).format(DT0Format),
        endDate: endDate?.format(DT0Format) ?? moment(dateRange[1]).format(DT0Format),
      };
      if(activeTab === 1 && scatterPlotImages === ''){
        onLoadScatterPlot(values, enrichedMetadata)
      }
    }

  }, [activeTab, enrichedMetadata, activePointMetadata?.metadata, activePointMetadata?.userGroupPermissions, activePoint?.coordinates, validInSituPoint, dateRange])

  const onLoadScatterPlot = async (values: IGetBasisChartV2ApiPayload, enrichedMetadata: IEnrichedMetadata[]) => {
    setLoading(true)
    setScatterPlotImages('')
    setLinks([])
    try {
      const category = activePointMetadata?.bestDataset?.find(
        (bd: IBestDataset) => bd.id === values.datasetName
      );

      if (category !== undefined) {
        const task = enrichedMetadata.find(
          (task: IEnrichedMetadata) =>
            task.DatasetId === values.datasetName &&
            task.CoordinatesString === activePoint?.coordinates.join()
        );
        if(task){
          const res = await Api.chart.getScatterPlotInsitu(
            insituName ?? '',
            task.OrderId,
            values.parameters,
            values.startDate,
            values.endDate,
          )
          if(res?.img){
            setScatterPlotImages('data:image/png;base64,' + res.img)
          }
          if(res?.links){
            setLinks(res.links)
          }
        }
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }

  }

  const onChangeStartDate = (value: any) => {
    if (value) {
      setStartDate(value);
    }
  };

  const onChangeEndDate = (value: any) => {
    if (value) {
      setEndDate(value);
    }
  };

  const onVariableChange = (value: string) => {
    setVariable(value)
  }

  const onChangeParameter = (value: string) => {
    setActiveCategory(value)
  }

  const handleUpdate = () => {
    if (enrichedMetadata && activePointMetadata?.metadata && startDate && endDate) {
      const values: IGetBasisChartV2ApiPayload = {
        datasetName: activePointMetadata?.metadata?.DatasetId,
        parameters: variable,
        startDate: startDate?.format(DT0Format),
        endDate: endDate?.format(DT0Format),
      };
      onLoadScatterPlot(values, enrichedMetadata)
    }
  }

  const actionRows = (
    <Fragment>
      <Row className={!validInSituPoint ? styles.unactiveActionRow : styles.activeActionRow}>
        <Col span={6}>
          <Label label="Parameters" >
            <Select
              value={activeCategoryKey ? activeCategoryKey  as string : DownloadCheckboxKey.SW  as string}
              onChange={onChangeParameter}
              size="small"
              className={styles.field}
              disabled={!validInSituPoint || loading}
            >
              {Object.entries(DownloadCheckboxKey).map(([key, val]) => (
                <Select.Option key={key} value={val}>
                  {val}
                </Select.Option>
              ))}
            </Select>
          </Label>
        </Col>
        <Col span={6}>
          <Label label="Variables" >
            <Select
              value={variable}
              onChange={onVariableChange}
              size="small"
              disabled={!validInSituPoint || loading}
              className={styles.field}
            >
              {listParameters
                .map((item) => {
                  return (
                    <Select.Option key={item.id} value={item.name}>
                      {item.name}
                    </Select.Option>
                  );
                })}
            </Select>
          </Label>
        </Col>
        <Col span={6}>
          <Label label="Start Date">
            <DatePicker
              size="small"
              className={styles.field}
              value={moment(startDate)}
              onChange={onChangeStartDate}
              format={DateFormat}
              disabled={!validInSituPoint || loading}
            />
          </Label>
        </Col>
        <Col span={6}>
          <Label label="End Date">
            <DatePicker
              size="small"
              className={styles.field}
              value={moment(endDate)}
              onChange={onChangeEndDate}
              format={DateFormat}
              disabled={!validInSituPoint || loading}
            />
          </Label>
        </Col>
      </Row>
      <Row className={!validInSituPoint ? styles.unactiveActionRow : styles.activeActionRow}>
        <Col span={6} className={styles.buttonUpdateWrapper}>
          <Button
            type='primary'
            onClick={handleUpdate}
            disabled={!validInSituPoint || loading}
            >
            Update
          </Button>
          <AnalyticsDownloadLinks links={links} />
        </Col>
      </Row>
    </Fragment>
  );

  return (
    <ChartBox
      actionRow={actionRows}
      key={'Scatter Plot'}
      title={'Scatter Plot'}
      height={320}
      plot={
        <>
          {loading && <Spin indicator={<LoadingOutlined style={{ fontSize: 50 }} spin/>} />}
          {!loading && scatterPlotImages.trim() !== "" && (
            <img
              style={{ width: '500px', objectFit: 'scale-down', maxHeight: '65vh' }}
              src={scatterPlotImages}
              alt="ScatterPlot"
            />
          )}
          {!loading && scatterPlotImages === '' && (
            <Empty />
          )}
        </>
      }
      dataset=''
    />
  )
}


export default ScatterPlot
